// Данните на таблото — по ЕДИН източник на кутия, същите заявки (и същите
// ключове в кеша) като началните екрани на операциите: отвориш ли кутията,
// страницата вече има данните. Всяка кутия се зарежда сама — бавен или паднал
// източник не спира другите. В локален режим (каса без интернет) нищо не
// тръгва: касата не знае за склада.
//
// „Излишък от продажби" е единствената тежка заявка (BE прави replay на
// движенията по продукт) — тук тя е с 60 s свежест и без опресняване при
// връщане в таба; бутонът „Опресни" я тегли наново.
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { getInterchangeGroups, getShortages } from "../../../../api/inventory";
import { useIsEdgeLocal } from "../../../../hooks/useEdgeMode";
import {
  useInventorySettings,
  usePendingReports,
  useProducts,
  useWarehouses,
  useWriteoffs,
} from "../../../../hooks/useInventory";
import { usePayableDeliveries } from "../../../../hooks/useInventoryDeliveries";
import { useLatestStocktakes } from "../../../../hooks/useInventoryStocktakes";
import { useTransfersReport } from "../../../../hooks/useInventoryTransfers";
import { refreshOf } from "../../components/list/reportRefresh";
import { periodOfPreset, periodToParams } from "../../components/period/periodPresets";
import { useInventoryShell } from "../../components/shell/InventoryShellContext";
import { useDeliveryHomeDrafts } from "../../delivery/components/home/useDeliveryHomeDrafts";
import { useStocktakeHomeDrafts } from "../../stocktake/components/home/useStocktakeHomeDrafts";
import { useTransferHomeDrafts } from "../../transfer/components/home/useTransferHomeDrafts";
import { useWriteoffHomeDrafts } from "../../writeoff/components/home/useWriteoffHomeDrafts";
import { buildShortageRows } from "../../shortage/components/shortageShared";
import {
  loadableOf,
  summarizeDeliveryDrafts,
  summarizeInterchange,
  summarizePayable,
  summarizePosting,
  summarizeShortages,
  summarizeStocktakes,
  summarizeTransfersToday,
  summarizeWriteoffsToday,
  type DashboardSummaries,
} from "../dashboardModel";

// Излишъкът се смята тежко — не го теглим при всяко влизане в таблото
const SHORTAGES_STALE_MS = 60_000;

// Заявка, която „готова" и с грешка (кутията пада на резерв, не на „не се зареди")
const settled = <T>(query: { data?: T; isError: boolean }, fallback: T) => ({
  data: query.data ?? (query.isError ? fallback : undefined),
  isError: false,
});

export const useDashboardSources = () => {
  const isLocal = useIsEdgeLocal();
  const { warehouseId } = useInventoryShell();
  const now = dayjs();
  const todayParams = periodToParams(periodOfPreset("today", now));

  // 1 Разнасяне — същият списък като страницата (най-старият, закъснелите)
  const pending = usePendingReports(!isLocal);
  // 2 Доставка — черновите (моята + на колегите) и модалите на началния екран
  const deliveryDrafts = useDeliveryHomeDrafts();
  const draftsQuery = deliveryDrafts.query;
  // Незавършените листове на изписване, инвентаризация и прехвърляне —
  // „Продължи" в кутиите им (същите заявки като на началните им екрани)
  const writeoffDrafts = useWriteoffHomeDrafts();
  const stocktakeDrafts = useStocktakeHomeDrafts();
  const transferDrafts = useTransferHomeDrafts();
  // 3 Плащане
  const payable = usePayableDeliveries();
  // 4 Изписване — днес, текущият склад (като началния екран)
  const warehouses = useWarehouses();
  const writeoffs = useWriteoffs(
    !isLocal && warehouseId != null ? { warehouseId, ...todayParams } : null,
  );
  // 5 Инвентаризация — последната по склад
  const latest = useLatestStocktakes();
  // 6 Прехвърляне — днес, всички складове (като началния екран)
  const transfers = useTransfersReport(todayParams);
  // 7 Излишък — бруто по ДДС ставката на продукта, както страницата
  const shortages = useQuery({
    queryKey: ["inventory", "shortages"],
    queryFn: () => getShortages(),
    enabled: !isLocal,
    staleTime: SHORTAGES_STALE_MS,
    refetchOnWindowFocus: false,
  });
  const products = useProducts(!isLocal);
  const { data: settings } = useInventorySettings();
  // 8 Взаимозаменяеми
  const groups = useQuery({
    queryKey: ["inventory", "interchange-groups"],
    queryFn: () => getInterchangeGroups(),
    enabled: !isLocal,
    staleTime: 60_000,
  });

  // Без склад (ресторант без складове) изписване няма откъде — нула, не „зарежда"
  const writeoffsSource =
    warehouseId == null
      ? { data: warehouses.data ? [] : undefined, isError: warehouses.isError }
      : writeoffs;

  const summaries: DashboardSummaries = {
    posting: loadableOf([pending], isLocal, () => summarizePosting(pending.data ?? [], now)),
    // До BE без черновите (404) началният екран работи с локалната — и таблото
    delivery: loadableOf([settled(draftsQuery, [])], isLocal, () =>
      summarizeDeliveryDrafts(deliveryDrafts.mine, deliveryDrafts.others),
    ),
    "delivery-payments": loadableOf([payable], isLocal, () =>
      summarizePayable(payable.data ?? [], now),
    ),
    writeoff: loadableOf([writeoffsSource], isLocal, () =>
      summarizeWriteoffsToday(writeoffsSource.data ?? []),
    ),
    // + незавършените броения (мои и на колегите) — кутията е червена при тях;
    // до BE без черновите — само локалната
    stocktake: loadableOf([warehouses, latest, settled(stocktakeDrafts.query, [])], isLocal, () =>
      summarizeStocktakes(warehouses.data ?? [], latest.data ?? [], now, {
        mine: stocktakeDrafts.mine,
        others: stocktakeDrafts.others,
      }),
    ),
    transfer: loadableOf([transfers], isLocal, () =>
      summarizeTransfersToday(transfers.data ?? [], now),
    ),
    // Продуктите дават само ДДС ставката — без тях сумата е нето, не „грешка"
    shortage: loadableOf([shortages, settled(products, [])], isLocal, () =>
      summarizeShortages(
        buildShortageRows(
          shortages.data ?? [],
          new Map((products.data ?? []).map((product) => [product.id, product])),
          settings?.vatRegistered ?? true,
        ),
      ),
    ),
    interchangeables: loadableOf([groups], isLocal, () => summarizeInterchange(groups.data ?? [])),
  };

  // Черновите на другите листове не са тук: опресняват се сами на 30 s, а
  // въртящото се „Опресни" на всяко тяхно опресняване би мигало постоянно
  const { refresh, refreshing } = refreshOf(
    pending,
    draftsQuery,
    payable,
    writeoffs,
    latest,
    transfers,
    shortages,
    groups,
  );

  const drafts = {
    delivery: deliveryDrafts,
    writeoff: writeoffDrafts,
    stocktake: stocktakeDrafts,
    transfer: transferDrafts,
  };
  return { summaries, drafts, isLocal, refresh, refreshing };
};
